import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import type { getIndexChartResponseSuccess } from '#/api/generated/indices/indices';
import {
  getGetIndexChartQueryKey,
  useGetIndexChart,
} from '#/api/generated/indices/indices';
import { Spinner } from '#/components/ui/Spinner';
import type { TabItem } from '#/components/ui/Tabs';
import { isSuccessResponse } from '#/lib/api-client';
import { MDC_MEASURE, startMeasure } from '#/lib/performance';

import {
  normalizeIndexChartData,
  normalizeSeriesForComparison,
} from './normalize';
import {
  CHART_TIMEFRAME_TABS,
  getChartQueryPolicy,
  getChartTimeframeBounds,
  getChartTimeframeParams,
} from './timeframes';
import { CHART_HEIGHT, TimeseriesChart } from './TimeseriesChart';
import type {
  ChartTimeframeId,
  ComparisonIndexId,
  NormalizedTimeseriesSeries,
  TimeseriesPoint,
  TimeseriesSeries,
  TooltipField,
} from './types';

type ChartQueryParams = ReturnType<typeof getChartTimeframeParams>;

type ChartSuccessResponse<TChartData> = {
  status: 200;
  data: TChartData;
};

type ChartQueryState<TChartData> = {
  data?:
    | ChartSuccessResponse<TChartData>
    | { status: number; data: unknown }
    | null;
  isError: boolean;
  isFetching: boolean;
  isPlaceholderData?: boolean;
  isPending: boolean;
};

type ChartQueryHook<TChartData> = (
  symbol: string,
  params: ChartQueryParams,
  options: {
    query: {
      staleTime: number;
      gcTime: number;
      refetchOnWindowFocus: false;
      refetchOnReconnect: false;
      placeholderData?: typeof keepPreviousData;
    };
  },
) => ChartQueryState<TChartData>;

const COMPARISON_INDEX_OPTIONS: Array<{
  id: ComparisonIndexId;
  label: string;
  subtitle: string;
}> = [
  { id: 's_p_500', label: 'S&P 500', subtitle: 'Comparison Index' },
  { id: 'nasdaq', label: 'Nasdaq', subtitle: 'Comparison Index' },
  { id: 'dow_jones', label: 'Dow Jones', subtitle: 'Comparison Index' },
];

const MAX_COMPARISON_INDEXES = 2;

function isMatchingIndexChartQueryKey(
  queryKey: readonly unknown[] | undefined,
  indexId: ComparisonIndexId,
  params: ChartQueryParams,
) {
  const expectedQueryKey = getGetIndexChartQueryKey(indexId, params);

  if (!queryKey || queryKey.length !== expectedQueryKey.length) {
    return false;
  }

  return queryKey.every((value, index) => {
    const expectedValue = expectedQueryKey[index];

    if (
      value != null &&
      expectedValue != null &&
      typeof value === 'object' &&
      typeof expectedValue === 'object'
    ) {
      return JSON.stringify(value) === JSON.stringify(expectedValue);
    }

    return value === expectedValue;
  });
}

function ChartUnavailableState({ message }: { message: string }) {
  return (
    <div className="p-gap-inset px-16">
      <div
        className="border-divider-subtle bg-slate-10 text-fg-muted flex items-center justify-center rounded border border-dashed px-16 text-center text-sm"
        style={{ height: CHART_HEIGHT }}
      >
        {message}
      </div>
    </div>
  );
}

function ChartLoadingState() {
  return (
    <div className="p-gap-inset px-16">
      <div
        className="border-divider-subtle bg-slate-10 text-fg-muted flex items-center justify-center rounded border border-dashed px-16 text-center text-sm"
        style={{ height: CHART_HEIGHT }}
      >
        <Spinner label="Loading chart data…" />
      </div>
    </div>
  );
}

interface ProductTypeChartSectionProps<TChartData> {
  symbol: string;
  initialTimeframe?: ChartTimeframeId;
  tabs?: TabItem<ChartTimeframeId>[];
  useChartQuery: ChartQueryHook<TChartData>;
  normalizeChartData: (chartData?: TChartData | null) => TimeseriesPoint[];
  tooltipFields?: TooltipField[];
  /** From the detail endpoint */
  previousClose?: number | null;
}

export function ProductTypeChartSection<TChartData>({
  symbol,
  initialTimeframe = '5Y',
  tabs = CHART_TIMEFRAME_TABS,
  useChartQuery,
  normalizeChartData,
  tooltipFields,
  previousClose,
}: ProductTypeChartSectionProps<TChartData>) {
  const [activeTimeframe, setActiveTimeframe] =
    useState<ChartTimeframeId>(initialTimeframe);
  const [displayedTimeframe, setDisplayedTimeframe] =
    useState<ChartTimeframeId>(initialTimeframe);
  const [selectedComparisonIndexes, setSelectedComparisonIndexes] = useState<
    ComparisonIndexId[]
  >([]);
  const queryPolicy = getChartQueryPolicy(activeTimeframe);
  const timeframeParams = getChartTimeframeParams(activeTimeframe);
  const displayedTimeframeBounds = useMemo(() => {
    return getChartTimeframeBounds(displayedTimeframe);
  }, [displayedTimeframe]);

  const queryOptions = {
    query: {
      staleTime: queryPolicy.staleTime,
      gcTime: queryPolicy.gcTime,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      placeholderData: keepPreviousData,
    },
  } as const;

  const query = useChartQuery(symbol, timeframeParams, queryOptions);

  const firstComparisonIndex = selectedComparisonIndexes[0];
  const secondComparisonIndex = selectedComparisonIndexes[1];

  const firstComparisonQuery = useGetIndexChart(
    firstComparisonIndex ?? 's_p_500',
    timeframeParams,
    {
      query: {
        staleTime: queryPolicy.staleTime,
        gcTime: queryPolicy.gcTime,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: Boolean(firstComparisonIndex),
        placeholderData: (previousData, previousQuery) => {
          if (!firstComparisonIndex) {
            return undefined;
          }

          return isMatchingIndexChartQueryKey(
            previousQuery?.queryKey,
            firstComparisonIndex,
            timeframeParams,
          )
            ? previousData
            : undefined;
        },
      },
    },
  );

  const secondComparisonQuery = useGetIndexChart(
    secondComparisonIndex ?? 'nasdaq',
    timeframeParams,
    {
      query: {
        staleTime: queryPolicy.staleTime,
        gcTime: queryPolicy.gcTime,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: Boolean(secondComparisonIndex),
        placeholderData: (previousData, previousQuery) => {
          if (!secondComparisonIndex) {
            return undefined;
          }

          return isMatchingIndexChartQueryKey(
            previousQuery?.queryKey,
            secondComparisonIndex,
            timeframeParams,
          )
            ? previousData
            : undefined;
        },
      },
    },
  );

  const response = isSuccessResponse(query.data) ? query.data : null;
  const isPrimaryChartUnavailable =
    !response && !query.isFetching && !query.isPending;

  // Opens `mdc:chart-render` during the render pass that first sees resolved
  // chart data — normalization and Recharts' own render are what the measure is
  // meant to capture, so an effect here would start the clock too late.
  // `startMeasure` is idempotent, which keeps repeated renders harmless.
  if (response) {
    startMeasure(MDC_MEASURE.chartRender);
  }

  useEffect(() => {
    if (query.isPlaceholderData) {
      return;
    }

    if (isSuccessResponse(query.data)) {
      setDisplayedTimeframe(activeTimeframe);
    }
  }, [activeTimeframe, query.data, query.isPlaceholderData]);

  const normalizedData = useMemo(() => {
    return response ? normalizeChartData(response.data) : [];
  }, [normalizeChartData, response]);

  const primarySeries = useMemo<TimeseriesSeries | null>(() => {
    if (!normalizedData.length) {
      return null;
    }

    return {
      id: symbol.toUpperCase(),
      label: symbol.toUpperCase(),
      kind: 'primary',
      points: normalizedData,
    };
  }, [normalizedData, symbol]);

  const comparisonSelections = useMemo(() => {
    const selections: Array<{
      id: ComparisonIndexId;
      option: (typeof COMPARISON_INDEX_OPTIONS)[number];
      query: typeof firstComparisonQuery;
      response: getIndexChartResponseSuccess | null;
    }> = [];

    if (firstComparisonIndex) {
      const option = COMPARISON_INDEX_OPTIONS.find(
        (comparisonOption) => comparisonOption.id === firstComparisonIndex,
      );

      if (option) {
        selections.push({
          id: firstComparisonIndex,
          option,
          query: firstComparisonQuery,
          response: isSuccessResponse(firstComparisonQuery.data)
            ? firstComparisonQuery.data
            : null,
        });
      }
    }

    if (secondComparisonIndex) {
      const option = COMPARISON_INDEX_OPTIONS.find(
        (comparisonOption) => comparisonOption.id === secondComparisonIndex,
      );

      if (option) {
        selections.push({
          id: secondComparisonIndex,
          option,
          query: secondComparisonQuery,
          response: isSuccessResponse(secondComparisonQuery.data)
            ? secondComparisonQuery.data
            : null,
        });
      }
    }

    return selections;
  }, [
    firstComparisonIndex,
    firstComparisonQuery,
    secondComparisonIndex,
    secondComparisonQuery,
  ]);

  const comparisonSeries = useMemo<TimeseriesSeries[]>(() => {
    return comparisonSelections.flatMap((selection) => {
      const chartResponse = selection.response as
        | getIndexChartResponseSuccess
        | null
        | undefined;

      if (!chartResponse) {
        return [];
      }

      const points = normalizeIndexChartData(chartResponse.data);

      if (!points.length) {
        return [];
      }

      return [
        {
          id: selection.id,
          label: selection.option.label,
          subtitle: selection.option.subtitle,
          kind: 'comparison' as const,
          points,
        },
      ];
    });
  }, [comparisonSelections]);

  const chartSeries = useMemo<TimeseriesSeries[]>(() => {
    return primarySeries ? [primarySeries, ...comparisonSeries] : [];
  }, [comparisonSeries, primarySeries]);

  const normalizedComparisonSeries = useMemo<
    NormalizedTimeseriesSeries[] | undefined
  >(() => {
    if (chartSeries.length < 2) {
      return undefined;
    }

    const normalizedSeries = normalizeSeriesForComparison(chartSeries);

    return normalizedSeries.length > 1 ? normalizedSeries : undefined;
  }, [chartSeries]);

  const comparisonCards = useMemo(() => {
    return comparisonSelections.map((selection) => {
      const normalizedSeries = normalizedComparisonSeries?.find(
        (series) => series.id === selection.id,
      );

      return {
        id: selection.id,
        label: selection.option.label,
        subtitle: selection.option.subtitle,
        isError:
          !selection.response &&
          !selection.query.isFetching &&
          !selection.query.isPending,
        isLoading:
          !normalizedSeries &&
          (selection.query.isFetching || selection.query.isPending),
        latestPercentChange: normalizedSeries?.latestPercentChange,
        latestRawValue: normalizedSeries?.latestRawValue,
      };
    });
  }, [comparisonSelections, normalizedComparisonSeries]);

  const availableComparisonOptions = useMemo(
    () =>
      COMPARISON_INDEX_OPTIONS.filter(
        (option) => !selectedComparisonIndexes.includes(option.id),
      ),
    [selectedComparisonIndexes],
  );

  const canAddComparison =
    selectedComparisonIndexes.length < MAX_COMPARISON_INDEXES &&
    availableComparisonOptions.length > 0;
  const isAddComparisonDisabled = normalizedData.length === 0;

  function handleSelectComparison(indexId: ComparisonIndexId) {
    setSelectedComparisonIndexes((current) => {
      if (
        current.includes(indexId) ||
        current.length >= MAX_COMPARISON_INDEXES
      ) {
        return current;
      }

      return [...current, indexId];
    });
  }

  function handleRemoveComparison(indexId: ComparisonIndexId) {
    setSelectedComparisonIndexes((current) =>
      current.filter((currentId) => currentId !== indexId),
    );
  }

  if (isPrimaryChartUnavailable) {
    return (
      <ChartUnavailableState message="Chart data could not be loaded right now." />
    );
  }

  if (!response) {
    return <ChartLoadingState />;
  }

  return (
    <TimeseriesChart
      data={normalizedData}
      activeTimeframe={activeTimeframe}
      displayedTimeframe={displayedTimeframe}
      isLoading={query.isFetching || query.isPending}
      xAxisDomain={
        displayedTimeframe === 'ALL'
          ? undefined
          : [
              displayedTimeframeBounds.startDate.getTime(),
              displayedTimeframeBounds.endDate.getTime(),
            ]
      }
      comparisonControls={{
        cards: comparisonCards,
        availableOptions: availableComparisonOptions,
        canAddComparison,
        isAddComparisonDisabled,
        onRemoveComparison: handleRemoveComparison,
        onSelectComparison: handleSelectComparison,
      }}
      normalizedSeries={normalizedComparisonSeries}
      onTimeframeChange={setActiveTimeframe}
      tabs={tabs}
      tooltipFields={tooltipFields}
      previousClose={previousClose}
    />
  );
}
